import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {AssignmentCompletedNotification} from "@khan/assignments";
import {renderToast} from "@khan/component-toast";
import {
    updateConversationWithSystemMessage,
    useSendGuideEvent,
    useSubmitAssignmentMutation,
    type StudentActivityAssignmentData,
} from "@khan/guide-core";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {RequestInfo} from "@khan/shared-core";

interface Props {
    style?: StyleType;
    disabled: boolean;
    studentAssignmentData: StudentActivityAssignmentData | null;
    currentThreadSubmitted: boolean;
    setCurrentThreadSubmitted: (threadSubmitted: boolean) => void;
}

export const SubmitAssignmentModalLauncher = ({
    style,
    disabled,
    studentAssignmentData,
    currentThreadSubmitted,
    setCurrentThreadSubmitted,
}: Props): React.ReactElement => {
    const isMobile = RequestInfo.isMobile;
    const threadId = useGuideState("threadId");
    const sendGuideEvent = useSendGuideEvent();
    const [isError, setIsError] = useState(false);
    const [submissionProcessing, setSubmissionProcessing] = useState(false);
    const [assignmentDataLoading, setAssignmentDataLoading] = useState(true);
    const [submittedInDifferentSession, setSubmittedInDifferentSession] =
        useState(false);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        if (studentAssignmentData) {
            setAssignmentDataLoading(false);

            setSubmittedInDifferentSession(!!studentAssignmentData.completedOn);
            if (
                studentAssignmentData.threadIDs?.find((id) => id === threadId)
            ) {
                setCurrentThreadSubmitted(true);
            }
        }
    }, [studentAssignmentData, threadId, setCurrentThreadSubmitted]);

    const submitAssignmentMutationFn = useSubmitAssignmentMutation();
    const submitAssignment = useCallback(
        async (closeModal: () => void) => {
            if (!studentAssignmentData || !threadId) {
                setIsError(true);
                return;
            }

            setSubmissionProcessing(true);

            try {
                const notification = await submitAssignmentMutationFn(
                    studentAssignmentData.id,
                    threadId,
                );
                if (notification) {
                    renderToast(
                        <AssignmentCompletedNotification
                            studentListName={notification.classroomName}
                            firstCompletion={notification.firstCompletion}
                        />,
                    );
                }
                setIsError(false);
                setCurrentThreadSubmitted(true);
                closeModal();

                publishAnalyticsEvent({
                    eventType: "AiGuideActivityAssignmentStudentSubmitted",
                    eventSchemaVersion: 1,
                    assignmentId: studentAssignmentData.id,
                    aiGuideThreadId: threadId,
                });
                GuideStateApi.set(
                    "conversation",
                    updateConversationWithSystemMessage(
                        GuideStateApi.latest().conversation,
                        t`Your assignment has been submitted.`,
                        false,
                    ),
                );

                await sendGuideEvent("submit_assignment");
            } catch (error: any) {
                setIsError(true);
            }

            setSubmissionProcessing(false);
        },
        [
            publishAnalyticsEvent,
            submitAssignmentMutationFn,
            studentAssignmentData,
            threadId,
            sendGuideEvent,
            setCurrentThreadSubmitted,
        ],
    );

    // Reset submission tracking if the thread ever changes
    const prevThreadId = useRef(threadId);
    useEffect(() => {
        if (threadId !== prevThreadId.current) {
            setCurrentThreadSubmitted(false);
        }
    }, [threadId, setCurrentThreadSubmitted]);

    return (
        <View style={style}>
            <ModalLauncher
                modal={({closeModal}) => (
                    <OnePaneDialog
                        title={t`Ready to submit?`}
                        style={styles.modal}
                        content={
                            <View>
                                <LabelMedium>
                                    {t`Are you ready to submit your assignment? Once submitted, you can't make changes. Please take a moment to review your work.`}
                                </LabelMedium>
                                {submittedInDifferentSession && (
                                    <LabelMedium style={styles.error}>
                                        {t`Your previous submission will be lost if you resubmit. Are you sure you want to resubmit?`}
                                    </LabelMedium>
                                )}
                                {isError && (
                                    <LabelMedium style={styles.error}>
                                        {t`There was an error submitting your assignment. Please try again later.`}
                                    </LabelMedium>
                                )}
                            </View>
                        }
                        footer={
                            <View style={styles.footer}>
                                <Button
                                    kind="tertiary"
                                    onClick={closeModal}
                                    style={styles.notYetButton}
                                >
                                    {t`Not yet`}
                                </Button>
                                <Button
                                    kind="primary"
                                    onClick={() => {
                                        submitAssignment(closeModal);
                                    }}
                                    spinner={submissionProcessing}
                                    testId="submit-assignment-button"
                                >
                                    {t`Yes, submit now`}
                                </Button>
                            </View>
                        }
                    />
                )}
            >
                {({openModal}) => (
                    <Button
                        onClick={openModal}
                        kind="secondary"
                        disabled={disabled || currentThreadSubmitted}
                        testId="submit-assignment-modal-launcher"
                        spinner={assignmentDataLoading}
                    >
                        {currentThreadSubmitted
                            ? t`Submitted`
                            : submittedInDifferentSession
                              ? isMobile
                                  ? t`Resubmit`
                                  : t`Resubmit this assignment`
                              : isMobile
                                ? t`Submit`
                                : t`Submit this assignment`}
                    </Button>
                )}
            </ModalLauncher>
        </View>
    );
};

const styles = StyleSheet.create({
    error: {
        color: color.red,
    },
    modal: {
        height: 272,
    },
    notYetButton: {
        marginInlineEnd: spacing.medium_16,
    },
    footer: {
        flexDirection: "row",
    },
});
